/* eslint-disable no-magic-numbers */
import { RgbColor } from 'solid-color';
import tinycolor from 'tinycolor2';

export const RGB_MAX_VALUE = 255;

/**
 * Color used when highlighting surfaces as measurement references.
 * Matches the yellow used for area measurement polygons in the viewer.
 */
export const MEASUREMENT_HIGHLIGHT_COLOR = 0xffcc00;

/**
 * Opacity applied when surfaces are highlighted as measurement reference surfaces.
 */
export const MEASUREMENT_HIGHLIGHT_OPACITY = 0.5;

/**
 * Converts an RGB color object to a hex string
 * @param color RGB color representation
 * @returns A hex string (prefixed with '#') of the color
 */
export const rgbColorToCssHexString = (color: RgbColor): string => {
  return `#${numberToHexString(color.r)}${numberToHexString(
    color.g
  )}${numberToHexString(color.b)}`;
};

/**
 * Converts a number to a hex string
 * @param value The number to convert
 * @param padLength The length of the hex string (default is 2)
 * @returns A hex string of the number
 */
export const numberToHexString = (
  value: number,
  // eslint-disable-next-line no-magic-numbers
  padLength: number = 2
): string => {
  const base: number = 16;
  return value.toString(base).padStart(padLength, '0');
};

/**
 * Converts a RGB color hex string to a single integer value.
 * @see numberToCssHexString
 * @param color Color as a hex string
 * @returns The RGB integer representation of that color, or 0 (black) if it is not a valid color string
 */
export const rgbCssHexToNumber = (color: string): number => {
  const c = tinycolor(color);
  if (c.isValid()) {
    const rgb = c.toRgb();
    return (rgb.r << 16) | (rgb.g << 8) | rgb.b;
  }
  return 0;
};

/**
 * Formats an RgbColor object into a single number, using established color representaiton format standards.
 * @param color RgbColor object
 * @returns Single number with r, g, and b components bitshifted into their correct positions
 */
export const rgbToNumber = (color: RgbColor): number => {
  return (color.r << 16) | (color.g << 8) | color.b;
};

/**
 * Converts a single number (min 24 bit integer with each color stored in little-endian bit positions) to a hex color string for CSS styling
 * (see https://www.csfieldguide.org.nz/en/chapters/data-representation/images-and-colours/#how-do-we-use-bits-to-represent-the-colour for more info on structure)
 * @param color Color value as a number (rightmost 8 bits are the blue channel)
 */
export const numberToCssHexString = (color: number): string => {
  return rgbColorToCssHexString({
    r: (color >> 16) & 0xff,
    g: (color >> 8) & 0xff,
    b: color & 0xff,
  });
};

export const isValidColorHex = (colorHex: string): boolean => {
  // color hex string must always start with a '#' and have exactly 6 hex digits (0-9, a-f, case insensitive)
  const validHexReg = /^#[0-9a-f]{6}$/i;
  return validHexReg.test(colorHex);
};

/**
 * Normalizes any valid CSS color (named, 3-digit hex, 6-digit hex, etc.) to a 6-digit hex string (#rrggbb).
 * Used so layer/object colors are always in a form that the line-editing plugin and materials accept.
 * When returning the fallback, it is normalized via tinycolor so casing matches toHexString() (lowercase),
 * avoiding false diffs when comparing with styles that were set through the normal path.
 * @param color Input color (e.g. "yellow", "#FF0", "#FFFF00")
 * @param fallback Fallback when input is invalid or empty
 * @returns 6-digit hex string including "#", or normalized fallback
 */
export const normalizeToSixDigitHex = (
  color: string | null | undefined,
  fallback: string
): string => {
  if (color == null || String(color).trim() === '') {
    const fallbackParsed = tinycolor(fallback);
    return fallbackParsed.isValid() ? fallbackParsed.toHexString() : fallback;
  }
  const c = tinycolor(color);
  if (!c.isValid()) {
    const fallbackParsed = tinycolor(fallback);
    return fallbackParsed.isValid() ? fallbackParsed.toHexString() : fallback;
  }
  return c.toHexString();
};

const HEX_RGB_CHARS = 6;
const HEX_ALPHA_CHARS = 2;
const FULL_ALPHA_HEX = 'FF';

/**
 * Converts an 8-digit `RRGGBBAA` hex string (alpha last, with or without a
 * leading `#`) to a 6-digit CSS hex (`#rrggbb`), dropping the alpha channel.
 * Used for `<input type="color">` values and chart colors, which do not accept
 * the 8-digit form.
 * @param rgbaHex Color as an 8-digit hex, e.g. "EBEBEBFF" or "#FFA800FF"
 * @returns 6-digit CSS hex string including "#"
 */
export const rgbaHexToCssHex = (rgbaHex: string): string =>
  `#${rgbaHex.replace(/^#/, '').substring(0, HEX_RGB_CHARS)}`;

/**
 * Re-attaches the alpha channel from `sourceRgbaHex` onto a 6-digit CSS hex,
 * returning an 8-digit `RRGGBBAA` string (no `#`). Falls back to full opacity
 * when the source carries no alpha channel. Any leading `#` on the source is
 * stripped first so the alpha is read from the correct offset.
 * @param cssHex 6-digit CSS hex from a color input, e.g. "#ffa800"
 * @param sourceRgbaHex Original 8-digit hex the alpha is preserved from
 * @returns 8-digit `RRGGBBAA` string without "#"
 */
export const cssHexToRgbaHex = (
  cssHex: string,
  sourceRgbaHex: string
): string => {
  const cleaned = cssHex.replace(/^#/, '').toUpperCase();
  const source = sourceRgbaHex.replace(/^#/, '').toUpperCase();
  const alpha =
    source.length >= HEX_RGB_CHARS + HEX_ALPHA_CHARS
      ? source.substring(HEX_RGB_CHARS, HEX_RGB_CHARS + HEX_ALPHA_CHARS)
      : FULL_ALPHA_HEX;
  return `${cleaned}${alpha}`;
};

export type ColorRep = number | RGBFormat | string;
export type RGBFormat = { r: number; g: number; b: number };
export type RGBAFormat = { r: number; g: number; b: number; a: number };

/**
 * Ensures a color has an alpha channel.
 * If alpha is already present, it's preserved. Otherwise, adds default full opacity (255).
 */
export function ensureAlpha(
  color: RGBFormat | RGBAFormat | undefined
): RGBAFormat | undefined {
  if (!color) return undefined;
  return { ...color, a: 'a' in color ? color.a : RGB_MAX_VALUE };
}

/**
 * Checks whether two colors are equivalent. Inputs can be a RGB composite number, RGB object, or hex string
 */
export const colorsAreEqual = (first: ColorRep, second: ColorRep): boolean => {
  const firstColor = tinycolor(
    typeof first === 'number' ? numberToCssHexString(first) : first
  );
  const secondColor = tinycolor(
    typeof second === 'number' ? numberToCssHexString(second) : second
  );
  if (firstColor.isValid() && secondColor.isValid()) {
    return firstColor.toHex() === secondColor.toHex();
  }
  return false;
};

export const isDarkColor = (color: string): boolean =>
  tinycolor(color).isDark();

export const rgbFormatToHexString = (color: RGBFormat): string => {
  return tinycolor(color).toHexString();
};

export const hexStringToRGBFormat = (hex: string): RGBFormat => {
  const color = tinycolor(hex).toRgb();
  return { r: color.r, g: color.g, b: color.b };
};

export const lerpColorRGB = (
  a: RGBFormat,
  b: RGBFormat,
  t: number
): RGBFormat => {
  const red = Math.round(a.r + t * (b.r - a.r));
  const green = Math.round(a.g + t * (b.g - a.g));
  const blue = Math.round(a.b + t * (b.b - a.b));
  return { r: red, g: green, b: blue };
};

/**
 * Normalize RGB color from 0-255 range to 0-1 range
 * @param color RGB color with values in 0-255 range
 * @returns RGB color with values in 0-1 range
 */
export const normalizeRgbColor = (color: RGBFormat): RGBFormat => {
  return {
    r: color.r / RGB_MAX_VALUE,
    g: color.g / RGB_MAX_VALUE,
    b: color.b / RGB_MAX_VALUE,
  };
};

/**
 * Denormalize RGB color from 0-1 range to 0-255 range
 * @param color RGB color with values in 0-1 range
 * @returns RGB color with values in 0-255 range
 */
export const denormalizeRgbColor = (color: RGBFormat): RGBFormat => {
  return {
    r: Math.round(color.r * RGB_MAX_VALUE),
    g: Math.round(color.g * RGB_MAX_VALUE),
    b: Math.round(color.b * RGB_MAX_VALUE),
  };
};

/**
 * Converts an RGB color object to a CSS rgb() string
 * @param color RGB color representation
 * @returns A CSS rgb() string format like "rgb(255,128,0)"
 */
export const getRGBColorString = (color: RgbColor): string => {
  const rColor = String(color.r);
  const gColor = String(color.g);
  const bColor = String(color.b);
  return `rgb(${rColor},${gColor},${bColor})`;
};
